import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import { 
  Calendar, Clock, MapPin, Coffee, Flag, Award, 
  Mail, Phone, HelpCircle, ChevronRight, Check
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BISN_LOGO = process.env.REACT_APP_BISN_LOGO_URL;

const schedule = [
  { time: "6:30 AM", event: "Registration & Breakfast Opens", icon: Coffee },
  { time: "7:30 AM", event: "Driving Range Opens", icon: Flag },
  { time: "8:00 AM", event: "Shotgun Start", icon: Clock },
  { time: "12:30 PM", event: "Lunch Service Begins", icon: Coffee },
  { time: "1:30 PM", event: "Golf Concludes", icon: Flag },
  { time: "2:00 PM", event: "Awards & Prizes Ceremony", icon: Award },
  { time: "3:30 PM", event: "Event Closes", icon: Check }
];

const faqs = [
  {
    question: "What is the Toronto Police BISN?",
    answer: "The Black Internal Support Network (BISN) is a support network within the Toronto Police Service available to both civilian and uniform members, particularly those who self-identify as Black. BISN is committed to promoting a more inclusive workplace through shared knowledge, mutual respect, mentorship, and coaching."
  },
  {
    question: "What is the Sickle Cell Association of Ontario (SCAO)?",
    answer: "The Sickle Cell Association of Ontario (SCAO) is a registered charity (Registration #: 890327067 RR0001) dedicated to supporting individuals and families affected by sickle cell disease. SCAO provides patient support services, funds research, and raises public awareness. Tax receipts for donations over $20 are issued by SCAO. Visit sicklecellon.ca to learn more."
  },
  {
    question: "What is included in the registration fee?",
    answer: "Your registration includes 18 holes of golf at Royal Woodbine Golf Club, a golf cart, driving range access, lunch, and entry to the awards ceremony and 19th Hole Happy Hour."
  },
  {
    question: "Can I register as an individual?",
    answer: "Yes! You can register as an individual golfer and we'll pair you with other individuals to form a foursome, or you can register a full foursome together."
  },
  {
    question: "What is the cancellation policy?",
    answer: "Cancellations made 14+ days before the event receive a full refund minus a $25 admin fee. Cancellations within 14 days are non-refundable but can be transferred to another participant."
  },
  {
    question: "Is there a dress code?",
    answer: "Yes, standard golf attire is required. Collared shirts, golf shorts or pants, and soft-spike golf shoes. No denim, athletic shorts, or t-shirts on the course."
  },
  {
    question: "What happens in case of rain?",
    answer: "The event proceeds rain or shine. In case of severe weather, Royal Woodbine Golf Club will make the call on course closure. If the event is cancelled, registrants will receive a full refund."
  },
  {
    question: "Can I bring guests who aren't golfing?",
    answer: "Non-golfing guests are welcome to join for the awards dinner at an additional cost. Please contact us for details."
  },
  {
    question: "Will I receive a charitable tax receipt?",
    answer: "Yes, charitable tax receipts for contributions over $20 (less 3% processing fee) will be issued by the Sickle Cell Association of Ontario (Charitable Registration #: 890327067 RR0001)."
  }
];

export default function InfoPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4] py-12 md:py-20">
      <div className="container-main">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
            Event Details
          </p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-6" data-testid="info-title">
            Everything You Need to Know
          </h1>
        </div>

        {/* BISN Section - Compact */}
        <section className="mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#2A4430] to-[#3A5F40] rounded-xl p-5 md:p-6 text-white">
              <div className="flex flex-col md:flex-row items-center gap-5">
                <div className="flex-shrink-0 bg-white rounded-xl p-3">
                  <img 
                    src={BISN_LOGO}
                    alt="Black Internal Support Network - Toronto Police Service"
                    className="h-16 md:h-20 w-auto object-contain"
                    data-testid="info-bisn-logo"
                  />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-light mb-2">
                    About Toronto Police BISN
                  </h2>
                  <p className="text-sm text-white/90 leading-relaxed mb-3">
                    The <strong>Black Internal Support Network (BISN)</strong> is a support network within the Toronto Police Service 
                    committed to promoting a more inclusive workplace through shared knowledge, mutual respect, mentorship, and coaching.
                  </p>
                  <p className="text-xs text-[#C5A059] italic">
                    Leadership &bull; Unity &bull; Knowledge &bull; Excellence
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Event Details */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#3A5F40]/10 flex items-center justify-center mx-auto mb-4">
                <Calendar size={28} className="text-[#3A5F40]" />
              </div>
              <h3 className="text-xl mb-2 text-[#1C1C1C]">Date</h3>
              <p className="text-[#666055]" data-testid="event-date">Tuesday, August 11, 2026</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#3A5F40]/10 flex items-center justify-center mx-auto mb-4">
                <Clock size={28} className="text-[#3A5F40]" />
              </div>
              <h3 className="text-xl mb-2 text-[#1C1C1C]">Time</h3>
              <p className="text-[#666055]" data-testid="event-time">8:00 AM Shotgun Start</p>
            </div>
            <a 
              href="https://www.google.com/maps/place/Royal+Woodbine+Golf+Club/@43.6752,-79.5847,15z"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl border border-[#E6E2D6] p-6 text-center hover:shadow-lg transition-all duration-300 hover:border-[#3A5F40] group"
            >
              <div className="w-14 h-14 rounded-full bg-[#3A5F40]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#3A5F40]/20 transition-colors">
                <MapPin size={28} className="text-[#3A5F40]" />
              </div>
              <h3 className="text-xl mb-2 text-[#1C1C1C]">Location</h3>
              <p className="text-[#666055]" data-testid="event-location">
                Royal Woodbine Golf Club<br />
                <span className="text-sm">195 Galaxy Blvd, Toronto (Etobicoke), ON</span>
              </p>
              <p className="text-xs text-[#3A5F40] mt-2 group-hover:underline">View on Google Maps →</p>
            </a>
          </div>
        </section>

        {/* Schedule */}
        <section className="mb-16">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl text-center mb-8 text-[#1C1C1C]">Day Schedule</h2>
            <div className="bg-white rounded-2xl border border-[#E6E2D6] overflow-hidden">
              {schedule.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-4 ${
                    index !== schedule.length - 1 ? "border-b border-[#E6E2D6]" : ""
                  }`}
                  data-testid={`schedule-item-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#F9F8F4] flex items-center justify-center flex-shrink-0">
                    <item.icon size={18} className="text-[#3A5F40]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#1C1C1C]">{item.event}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#666055]">{item.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <HelpCircle size={40} className="mx-auto text-[#C5A059] mb-4" />
              <h2 className="text-3xl text-[#1C1C1C]">Frequently Asked Questions</h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-[#E6E2D6] px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-[#1C1C1C] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#666055] pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Contact */}
        <section>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-6 md:p-8">
              <div className="text-center mb-8">
                <Mail size={40} className="mx-auto text-[#3A5F40] mb-4" />
                <h2 className="text-3xl text-[#1C1C1C] mb-2">Get in Touch</h2>
                <p className="text-[#666055]">
                  Have questions? We're here to help.
                </p>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row justify-center gap-6 mb-8 pb-8 border-b border-[#E6E2D6]">
                <a
                  href="mailto:bisngolf@tps.ca"
                  className="flex items-center gap-2 text-[#3A5F40] hover:text-[#2A4430] transition-colors"
                  data-testid="contact-email"
                >
                  <Mail size={18} />
                  bisngolf@tps.ca
                </a>
              </div>

              {/* Contact Form */}
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#3A5F40] flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl mb-2 text-[#1C1C1C]">Message Sent!</h3>
                  <p className="text-[#666055] mb-4">We'll get back to you soon.</p>
                  <Button
                    onClick={() => {
                      setSubmitted(false);
                      setContactForm({ name: "", email: "", message: "" });
                    }}
                    variant="outline"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-[#666055]">Name</Label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Your name"
                      className="input-underline"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#666055]">Email</Label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="your@email.com"
                      className="input-underline"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#666055]">Message</Label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="How can we help?"
                      className="min-h-[120px] bg-[#F9F8F4] border border-[#E6E2D6] rounded-xl"
                      data-testid="contact-message-input"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    <ChevronRight size={20} />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
